import React, { useState, useEffect } from 'react';
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  Box,
  Button,
  Select,
  Tabs,
  DateRangePicker,
  FormField,
  Table,
  TextFilter,
  Pagination,
  CollectionPreferences,
  Modal,
  Alert,
  Popover,
} from "@cloudscape-design/components";
import { useTranslation } from 'react-i18next';
import * as api from "../api/api";

function InsightsCalls() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState({ label: "Todos", value: "all" });
  const [selectedAgent, setSelectedAgent] = useState({ label: "Todos", value: "all" });
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [activeTab, setActiveTab] = useState('generate');
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [filteringText, setFilteringText] = useState("");
  const PAGE_SIZE = 10;
  
  // Sorting state
  const [sortingColumn, setSortingColumn] = useState({ sortingColumn: "timestamp" });
  const [sortingDescending, setSortingDescending] = useState(true);
  
  // Column preferences
  const [preferences, setPreferences] = useState({
    pageSize: PAGE_SIZE,
    wrapLines: true,
    visibleContent: ["jobName", "supervisor", "agent", "insight", "timestamp"]
  });

  // Real data from API
  const [supervisors, setSupervisors] = useState([{ label: "Todos", value: "all" }]);
  const [agents, setAgents] = useState([{ label: "Todos", value: "all" }]);
  const [tableItems, setTableItems] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Estado para POST
  const [sendingPostRequest, setSendingPostRequest] = useState(false);
  const [showPostSuccessModal, setShowPostSuccessModal] = useState(false);
  const [postRequestResult, setPostRequestResult] = useState("");

  // Estado para verificación
  const [verifying, setVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState(null);

  // Estado para confirmación
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [processParams, setProcessParams] = useState(null);

  // Estado para historial
  const [historyItems, setHistoryItems] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyStats, setHistoryStats] = useState({
    count: 0,
    total_global_analyses: 0
  });
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [showHistoryDetailModal, setShowHistoryDetailModal] = useState(false);

  // Load data when component mounts
  useEffect(() => {
    fetchInitialData();
  }, []);
  
  // Refetch data when date range changes
  useEffect(() => {
    if (dateRange) {
      fetchData();
    }
  }, [dateRange]);

  // Fetch history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistoryData();
    }
  }, [activeTab]);

  // Load agents when supervisor changes
  useEffect(() => {
    const loadAgentsBySupervisor = async () => {
      if (selectedSupervisor && selectedSupervisor.value && selectedSupervisor.value !== 'all') {
        try {
          setLoadingAgents(true);
          console.log('Cargando agentes para supervisor:', selectedSupervisor.value);
          
          const response = await api.getAgentsBySupervisor(selectedSupervisor.value);
          
          if (response && response.agents && Array.isArray(response.agents)) {
            const agentOptions = [
              { label: "Todos", value: "all" },
              ...response.agents.map(agentName => ({
                label: agentName,
                value: agentName
              }))
            ];
            
            setAgents(agentOptions);
            setSelectedAgent({ label: "Todos", value: "all" });
            
            console.log(`Cargados ${response.agents.length} agentes para supervisor ${selectedSupervisor.value}`);
          } else {
            setAgents([{ label: "Todos", value: "all" }]);
            setSelectedAgent({ label: "Todos", value: "all" });
          }
        } catch (error) {
          console.error('Error loading agents by supervisor:', error);
          setAgents([{ label: "Todos", value: "all" }]);
          setSelectedAgent({ label: "Todos", value: "all" });
        } finally {
          setLoadingAgents(false);
        }
      } else {
        // Si supervisor es "Todos", cargar todos los agentes como antes
        if (tableItems.length > 0) {
          updateSupervisorsAndAgents(tableItems);
        }
      }
    };

    loadAgentsBySupervisor();
  }, [selectedSupervisor, tableItems]);

  // Update dropdown options from data
  const updateSupervisorsAndAgents = (items) => {
    const uniqueSupervisors = new Set();
    const uniqueAgents = new Set();
    
    // Extract unique supervisor and agent names from the data
    items.forEach(item => {
      if (item.supervisor) uniqueSupervisors.add(item.supervisor);
      if (item.agent) uniqueAgents.add(item.agent);
    });
    
    // Create supervisor options
    const supervisorOptions = [
      { label: "Todos", value: "all" },
      ...Array.from(uniqueSupervisors).map(name => ({
        label: name,
        value: name
      }))
    ];
    
    // Create agent options
    const agentOptions = [
      { label: "Todos", value: "all" },
      ...Array.from(uniqueAgents).map(name => ({
        label: name,
        value: name
      }))
    ];
    
    // Update state with new options
    setSupervisors(supervisorOptions);
    setAgents(agentOptions);
  };

  // Fetch initial data without date filters
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      console.log("Obteniendo datos iniciales...");
      
      const response = await api.getSummary();
      console.log("Respuesta de getSummary:", response);
      
      if (response && response.items && Array.isArray(response.items) && response.items.length > 0) {
        console.log(`Se encontraron ${response.items.length} elementos`);
        // Normalizar los datos para manejar campos con mayúsculas
        const normalizedItems = response.items.map(normalizeItemFields);
        setTableItems(normalizedItems);
        
        // Update dropdowns with data
        updateSupervisorsAndAgents(normalizedItems);
      } else {
        console.log("No se encontraron elementos en la respuesta inicial");
        setTableItems([]);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setTableItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate
      };
      
      console.log("Buscando datos con parámetros:", params);
      const response = await api.getSummary(params);
      console.log("Respuesta de getSummary con filtros:", response);
      
      if (response && response.items && Array.isArray(response.items) && response.items.length > 0) {
        console.log(`Se encontraron ${response.items.length} elementos con filtros`);
        // Normalizar los datos para manejar campos con mayúsculas
        const normalizedItems = response.items.map(normalizeItemFields);
        setTableItems(normalizedItems);
        
        // Update dropdowns with data
        updateSupervisorsAndAgents(normalizedItems);
      } else {
        console.log("No se encontraron elementos con los filtros aplicados");
        setTableItems([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setTableItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryData = async () => {
    try {
      setLoadingHistory(true);
      console.log("Obteniendo historial de análisis...");
      
      const response = await api.getSummaryHistory(); 
      console.log("Respuesta del historial:", response);
      
      if (response && response.history && Array.isArray(response.history)) {
        console.log(`Se encontraron ${response.history.length} elementos en el historial`);
        setHistoryItems(response.history);
        setHistoryStats({
          count: response.count || 0,
          total_global_analyses: response.total_global_analyses || 0
        });
      } else {
        console.log("No se encontraron elementos en el historial");
        setHistoryItems([]);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistoryItems([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Función para preparar los parámetros para las API
  const prepareParams = (baseParams) => {
    const params = { ...baseParams };
    
    // Eliminar valores 'all' para no enviarlos al backend
    if (params.supervisor === 'all') {
      delete params.supervisor;
    }
    
    if (params.agent === 'all') {
      delete params.agent;
    }
    
    return params;
  };

  // Función para procesar después de la confirmación
  const processAfterConfirmation = async () => {
    try {
      setGeneratingSummary(true);
      
      console.log("Generando resumen con parámetros confirmados:", processParams);
      
      // Usar postSummary para generar el resumen
      const response = await api.postSummary(processParams);
      console.log("Respuesta al generar resumen:", response);
      
      // Mostrar resultado en modal si hay un mensaje específico
      if (response && response.message) {
        setPostRequestResult(response.message);
        setShowPostSuccessModal(true);
      }
      
      // Para análisis globales, no actualizamos la tabla de elementos individuales
      // El resumen se guarda automáticamente en el historial por el backend
      console.log("Análisis global completado, resultado guardado en historial");

      // Cambiar automáticamente a la pestaña de historial después de generar
      setTimeout(() => {
        setActiveTab('history');
        fetchHistoryData(); // Actualizar el historial
      }, 1000);
      
    } catch (error) {
      console.error('Error generating summary:', error);
      // Mostrar error en modal
      setPostRequestResult(error.message || t("insights.postError", { defaultValue: "Error al generar resumen" }));
      setShowPostSuccessModal(true);
    } finally {
      setGeneratingSummary(false);
      setShowConfirmationModal(false);
    }
  };

  const generateSummary = async () => {
    // Solo se requiere el rango de fechas obligatoriamente
    if (!dateRange) return;

    try {
      // Iniciar verificación
      setVerifying(true);
      setVerificationMessage(null);
      setGeneratingSummary(true);
      
      // Preparar parámetros base
      const baseParams = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        supervisor: selectedSupervisor?.value,
        agent: selectedAgent?.value
      };
      
      // Preparar parámetros para verificación
      const verifyParams = prepareParams(baseParams);
      console.log("Verificando datos con parámetros:", verifyParams);
      
      // Paso 1: Verificar si hay datos disponibles
      const verifyResponse = await api.verifyData(verifyParams);
      console.log("Respuesta de verificación:", verifyResponse);
      
      // Si no se encontraron datos, mostrar mensaje y detener
      if (!verifyResponse.found) {
        setVerificationMessage(verifyResponse.message || "No se encontraron datos que coincidan con los filtros");
        setPostRequestResult(verifyResponse.message || "No se encontraron datos que coincidan con los filtros");
        setShowPostSuccessModal(true);
        return;
      }
      
      // Si hay datos, guardar el resultado y mostrar modal de confirmación
      setVerificationResult(verifyResponse);
      
      // Preparar parámetros para procesar y guardarlos para confirmar
      const params = prepareParams(baseParams);
      setProcessParams(params);
      
      // Mostrar modal de confirmación
      setShowConfirmationModal(true);
      
    } catch (error) {
      console.error('Error en verificación:', error);
      setPostRequestResult(error.message || "Error al verificar datos");
      setShowPostSuccessModal(true);
    } finally {
      setVerifying(false);
      setGeneratingSummary(false);
    }
  };

  // Función para normalizar los nombres de los campos (manejar mayúsculas/minúsculas)
  const normalizeItemFields = (item) => {
    return {
      id: item.JobName || item.jobName || Date.now().toString(),
      jobName: item.JobName || item.jobName || "",
      supervisor: item.Supervisor || item.supervisor || "",
      agent: item.Agent || item.agent || "",
      // Priorizar Summary, luego insightsPrompt
      insight: item.Summary || item.summary || item.insightsPrompt || item.InsightsPrompt || t("insights.noSummaryAvailable"),
      // Convertir timestamp de Unix a ISO si es necesario
      timestamp: typeof item.Timestamp === 'number' 
        ? new Date(item.Timestamp * 1000).toISOString() 
        : (item.Timestamp || item.timestamp || new Date().toISOString())
    };
  };
  
  const formatInsightWithPopover = (text) => {
    if (!text) return "-";
    
    const truncatedText = text.length > 50 ? text.substring(0, 50) + "..." : text;
    
    const formatFullText = (fullText) => {
      if (typeof fullText !== "string") return fullText;
      
      return (
        <div style={{ whiteSpace: "pre-wrap", maxWidth: "500px", lineHeight: "1.5" }}>
          {fullText.split("\n").map((line, index) => {
            if (/^\s*\d+\./.test(line)) {
              return <div key={index}><strong>{line}</strong></div>;
            } else if (/^\s*-/.test(line)) {
              return <div key={index} style={{ paddingLeft: "1em" }}>{line}</div>;
            } else {
              return <div key={index}>{line}</div>;
            }
          })}
        </div>
      );
    };

    return (
      <Popover
        dismissButton={false}
        position="right"
        size="large"
        triggerType="custom"
        content={formatFullText(text)}
      >
        <Button variant="link" style={{ 
          textAlign: "left", 
          whiteSpace: "normal",
          height: "auto",
          padding: "4px 8px",
          fontSize: "14px"
        }}>
          {truncatedText}
        </Button>
      </Popover>
    );
  };

  const isGenerateButtonEnabled = () => {
    // Solo se requiere el rango de fechas obligatoriamente
    return dateRange !== null && !generatingSummary && !verifying;
  };

  // Filtra los elementos según el texto de búsqueda
  const filteredItems = filteringText
    ? tableItems.filter(
        item =>
          (item.supervisor && item.supervisor.toLowerCase().includes(filteringText.toLowerCase())) ||
          (item.agent && item.agent.toLowerCase().includes(filteringText.toLowerCase())) ||
          (item.insight && item.insight.toLowerCase().includes(filteringText.toLowerCase())) ||
          (item.jobName && item.jobName.toLowerCase().includes(filteringText.toLowerCase()))
      )
    : tableItems;

  // Ordena los elementos según la columna seleccionada
  const sortedItems = [...filteredItems].sort((a, b) => {
    const column = sortingColumn.sortingColumn || "timestamp";
    let valueA = a[column] || "";
    let valueB = b[column] || "";
    
    // Para ordenamiento de fechas
    if (column === "timestamp") {
      valueA = valueA ? new Date(valueA).getTime() : 0;
      valueB = valueB ? new Date(valueB).getTime() : 0;
    }
    
    // Para ordenamiento de texto (usar localeCompare)
    if (typeof valueA === "string" && typeof valueB === "string") {
      const result = valueA.localeCompare(valueB);
      return sortingDescending ? -result : result;
    }
    
    // Para ordenamiento numérico
    if (valueA < valueB) return sortingDescending ? 1 : -1;
    if (valueA > valueB) return sortingDescending ? -1 : 1;
    return 0;
  });

  const pagesCount = Math.ceil(sortedItems.length / preferences.pageSize);
  const startIndex = (currentPageIndex - 1) * preferences.pageSize;
  const paginatedItems = sortedItems.slice(startIndex, startIndex + preferences.pageSize);

  // Formatear fecha/hora para mostrar
  const formatDateTime = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const date = new Date(timestamp);
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return timestamp;
    }
  };

  // Definición de columnas para la tabla
  const columnDefinitions = [
    {
      id: "jobName",
      header: t("contactTable.jobName"),
      cell: item => item.jobName || "-",
      sortingField: "jobName",
      width: 350
    },
    {
      id: "supervisor",
      header: t("insights.supervisor"),
      cell: item => item.supervisor || "-",
      sortingField: "supervisor",
      minWidth: 80
    },
    {
      id: "agent",
      header: t("insights.agent"),
      cell: item => item.agent || "-",
      sortingField: "agent",
      minWidth: 80
    },
    {
      id: "insight",
      header: t("insights.insight"),
      cell: item => formatInsightWithPopover(item.insight || ""),
      sortingField: "insight",
      width: 250,
      minWidth: 200
    },
    {
      id: "timestamp",
      header: t("insights.timestamp", { defaultValue: "Fecha/Hora" }),
      cell: item => formatDateTime(item.timestamp),
      sortingField: "timestamp",
      width: 160
    }
  ];

  // Manejador de cambio de ordenación
  const handleSortingChange = ({ detail }) => {
    setSortingColumn(detail);
    setSortingDescending(detail.sortingDescending);
  };

  // Manejador de cambio de preferencias
  const handlePreferencesChange = ({ detail }) => {
    setPreferences(detail);
    if (detail.pageSize !== preferences.pageSize) {
      setCurrentPageIndex(1);
    }
  };

  const renderTable = () => (
    <Table
      columnDefinitions={columnDefinitions}
      items={paginatedItems}
      loading={loading}
      loadingText={t("common.loading")}
      wrapLines={preferences.wrapLines}
      resizableColumns
      visibleColumns={preferences.visibleContent}
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      onSortingChange={handleSortingChange}
      filter={
        <TextFilter
          filteringText={filteringText}
          filteringPlaceholder={t("common.search")}
          filteringAriaLabel={t("common.search")}
          onChange={({ detail }) => setFilteringText(detail.filteringText)}
        />
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          pagesCount={pagesCount}
          onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          ariaLabels={{
            nextPageLabel: t("common.nextPage"),
            previousPageLabel: t("common.previousPage"),
            pageLabel: pageNumber => `${t("common.page")} ${pageNumber}`
          }}
        />
      }
      preferences={
        <CollectionPreferences
          title={t("contactTable.preferences", { defaultValue: "Preferencias" })}
          confirmLabel={t("contactTable.confirm", { defaultValue: "Confirmar" })}
          cancelLabel={t("contactTable.cancel", { defaultValue: "Cancelar" })}
          preferences={preferences}
          onConfirm={handlePreferencesChange}
          pageSizePreference={{
            title: t("contactTable.pageSize", { defaultValue: "Tamaño de página" }),
            options: [
              { value: 10, label: "10 registros" },
              { value: 20, label: "20 registros" },
              { value: 50, label: "50 registros" }
            ]
          }}
          wrapLinesPreference={{
            label: t("contactTable.wrapLines", { defaultValue: "Ajustar líneas" }),
            description: t("contactTable.wrapLinesDescription", { defaultValue: "Marca esta casilla para visualizar los títulos completos y ajustar las líneas automáticamente." })
          }}
          visibleContentPreference={{
            title: t("contactTable.selectVisibleColumns", { defaultValue: "Seleccionar columnas visibles" }),
            options: [
              {
                label: t("contactTable.callListProperties", { defaultValue: "Propiedades de la lista de llamadas" }),
                options: [
                  { id: "jobName", label: t("contactTable.jobName", { defaultValue: "Nombre del trabajo" }) },
                  { id: "supervisor", label: t("insights.supervisor", { defaultValue: "Supervisor" }) },
                  { id: "agent", label: t("insights.agent", { defaultValue: "Agente" }) },
                  { id: "insight", label: t("insights.insight", { defaultValue: "Insight" }) },
                  { id: "timestamp", label: t("insights.timestamp", { defaultValue: "Fecha/Hora" }) }
                ]
              }
            ]
          }}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          <b>{t("insights.noSummaries")}</b>
        </Box>
      }
    />
  );

  // Función para formatear rango de fechas
  const formatDateRange = (filters) => {
    if (!filters || !filters.startDate || !filters.endDate) return "-";
    
    try {
      const startDate = new Date(filters.startDate).toLocaleDateString('es-ES');
      const endDate = new Date(filters.endDate).toLocaleDateString('es-ES');
      return `${startDate} - ${endDate}`;
    } catch (error) {
      return "Rango inválido";
    }
  };

  // Función para ver detalles del historial
  const viewHistoryDetails = (item) => {
    setSelectedHistoryItem(item);
    setShowHistoryDetailModal(true);
  };

  // Definición de columnas para la tabla de historial
  const historyColumnDefinitions = [
    {
      id: "datetime",
      header: t("insights.datetime", { defaultValue: "Fecha/Hora" }),
      cell: item => item.datetime || "-",
      sortingField: "datetime",
      width: 170
    },
    {
      id: "supervisor",
      header: t("insights.supervisor", { defaultValue: "Supervisor" }),
      cell: item => item.filters?.supervisor || "Todos",
      sortingField: "filters.supervisor",
      width: 170
    },
    {
      id: "agent",
      header: t("insights.agent", { defaultValue: "Agente" }),
      cell: item => item.filters?.agent || "Todos",
      sortingField: "filters.agent",
      width: 150
    },
    {
      id: "dateRange",
      header: t("insights.dateRange", { defaultValue: "Rango Analizado" }),
      cell: item => formatDateRange(item.filters),
      minWidth: 150
    },
    {
      id: "itemsAnalyzed",
      header: t("insights.itemsAnalyzed", { defaultValue: "Items Analizados" }),
      cell: item => item.itemsAnalyzed || "-",
      sortingField: "itemsAnalyzed",
      width: 140
    },
    {
      id: "actions",
      header: t("insights.actions", { defaultValue: "Acciones" }),
      cell: item => (
        <Button
          variant="link"
          onClick={() => viewHistoryDetails(item)}
          iconName="zoom-in"
        />
      ),
      width: 120
    }
  ];

  // Renderizar tabla de historial
  const renderHistoryTable = () => (
    <div>
      {historyStats.total_global_analyses > 0 && (
        <Box margin={{ bottom: "m" }}>
          <Alert type="info">
            {t("insights.historyStats", { 
              defaultValue: `Mostrando ${historyStats.count} de ${historyStats.total_global_analyses} análisis globales total.`,
              count: historyStats.count,
              total: historyStats.total_global_analyses 
            })}
          </Alert>
        </Box>
      )}
      <Table
        columnDefinitions={historyColumnDefinitions}
        items={historyItems}
        loading={loadingHistory}
        loadingText={t("common.loading")}
        wrapLines={preferences.wrapLines}
        resizableColumns
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={handleSortingChange}
        filter={
          <TextFilter
            filteringText={filteringText}
            filteringPlaceholder={t("common.search")}
            filteringAriaLabel={t("common.search")}
            onChange={({ detail }) => setFilteringText(detail.filteringText)}
          />
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={Math.ceil(historyItems.length / preferences.pageSize)}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            ariaLabels={{
              nextPageLabel: t("common.nextPage"),
              previousPageLabel: t("common.previousPage"),
              pageLabel: pageNumber => `${t("common.page")} ${pageNumber}`
            }}
          />
        }
        preferences={
          <CollectionPreferences
            title={t("contactTable.preferences", { defaultValue: "Preferencias" })}
            confirmLabel={t("contactTable.confirm", { defaultValue: "Confirmar" })}
            cancelLabel={t("contactTable.cancel", { defaultValue: "Cancelar" })}
            preferences={preferences}
            onConfirm={handlePreferencesChange}
            pageSizePreference={{
              title: t("contactTable.pageSize", { defaultValue: "Tamaño de página" }),
              options: [
                { value: 10, label: "10 registros" },
                { value: 20, label: "20 registros" },
                { value: 50, label: "50 registros" }
              ]
            }}
            wrapLinesPreference={{
              label: t("contactTable.wrapLines", { defaultValue: "Ajustar líneas" }),
              description: t("contactTable.wrapLinesDescription", { defaultValue: "Marca esta casilla para visualizar los títulos completos y ajustar las líneas automáticamente." })
            }}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            <b>{t("insights.noHistoryItems", { defaultValue: "No hay elementos en el historial" })}</b>
          </Box>
        }
      />
    </div>
  );

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("insights.callsDescription")}
        >
          {t("insights.callsTitle")}
        </Header>
      }
    >
      <Container>
        <SpaceBetween size="l">
          <Tabs
            tabs={[
              {
                label: t("insights.generateSummary"),
                id: "generate",
                content: (
                  <SpaceBetween size="m">
                    <SpaceBetween size="m">
                      <DateRangePicker
                        onChange={({ detail }) => setDateRange(detail.value)}
                        value={dateRange}
                        placeholder={t("common.selectDateRange")}
                        i18nStrings={{
                          todayAriaLabel: t("common.today"),
                          nextMonthAriaLabel: t("common.nextMonth"),
                          previousMonthAriaLabel: t("common.previousMonth"),
                          customRelativeRangeDurationLabel: t("common.duration"),
                          customRelativeRangeOptionLabel: t("common.customRange"),
                          customRelativeRangeOptionDescription: t("common.customRangeDescription"),
                          customRelativeRangeUnitLabel: t("common.unit"),
                          formatRelativeRange: (e) => t("common.relativeRange", e),
                          formatUnit: (e, n) => t("common.unit", { unit: e, count: n }),
                          dateTimeConstraintText: t("common.dateTimeConstraint"),
                          relativeModeTitle: t("common.relativeMode"),
                          absoluteModeTitle: t("common.absoluteMode"),
                          relativeRangeSelectionHeading: t("common.relativeRangeSelection"),
                          startDateLabel: t("common.startDate"),
                          endDateLabel: t("common.endDate"),
                          startTimeLabel: t("common.startTime"),
                          endTimeLabel: t("common.endTime"),
                          clearButtonLabel: t("common.clear"),
                          cancelButtonLabel: t("common.cancel"),
                          applyButtonLabel: t("common.apply")
                        }}
                      />
                      <SpaceBetween direction="horizontal" size="m">
                        <FormField label={t("insights.supervisor")}>
                          <Select
                            selectedOption={selectedSupervisor}
                            onChange={({ detail }) => setSelectedSupervisor(detail.selectedOption)}
                            options={supervisors}
                            placeholder={t("insights.selectSupervisor")}
                            disabled={!dateRange}
                          />
                        </FormField>
                        <FormField label={t("insights.agent")}>
                          <Select
                            selectedOption={selectedAgent}
                            onChange={({ detail }) => setSelectedAgent(detail.selectedOption)}
                            options={agents}
                            placeholder={loadingAgents ? "Cargando agentes..." : t("insights.selectAgent")}
                            disabled={!dateRange || loadingAgents}
                            loading={loadingAgents}
                          />
                        </FormField>
                        <Button
                          variant="primary"
                          onClick={generateSummary}
                          disabled={!isGenerateButtonEnabled() || generatingSummary}
                          loading={generatingSummary}
                        >
                          {t("insights.generateSummary")}
                        </Button>
                      </SpaceBetween>
                    </SpaceBetween>
                    {renderTable()}
                  </SpaceBetween>
                )
              },
              {
                label: t("insights.summaryHistoryTab"),
                id: "history",
                content: renderHistoryTable()
              }
            ]}
            activeTabId={activeTab}
            onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          />
        </SpaceBetween>
      </Container>
      
      {/* Modal para mostrar resultado del POST */}
      <Modal
        visible={showPostSuccessModal}
        onDismiss={() => setShowPostSuccessModal(false)}
        header={t("insights.postRequestResult", { defaultValue: "Resultado de la solicitud" })}
        footer={
          <Box float="right">
            <Button onClick={() => setShowPostSuccessModal(false)} variant="primary">
              {t("common.ok", { defaultValue: "OK" })}
            </Button>
          </Box>
        }
      >
        <p>{postRequestResult}</p>
      </Modal>
      
      {/* Modal de confirmación para procesar */}
      <Modal
        visible={showConfirmationModal}
        onDismiss={() => setShowConfirmationModal(false)}
        header={t("insights.confirmProcessing", { defaultValue: "Confirmar procesamiento" })}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                onClick={() => setShowConfirmationModal(false)} 
                variant="link"
              >
                {t("common.cancel", { defaultValue: "Cancelar" })}
              </Button>
              <Button 
                onClick={processAfterConfirmation} 
                variant="primary"
                loading={generatingSummary}
              >
                {t("insights.confirm", { defaultValue: "Confirmar" })}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {verificationResult && (
          <SpaceBetween size="m">
            <h3>{t("insights.verificationSummary", { defaultValue: "Resumen de verificación" })}</h3>
            
            <Alert type="info">
              {verificationResult.message || t("insights.recordsFound", { defaultValue: "Registros encontrados para procesar" })}
            </Alert>
            
            <Box variant="div">
              <SpaceBetween size="xs">
                <div>
                  <strong>{t("insights.recordCount", { defaultValue: "Cantidad de registros" })}:</strong> {verificationResult.count || 0}
                </div>
              </SpaceBetween>
            </Box>
            
            <Box variant="div">
              <h4>{t("insights.filtersApplied", { defaultValue: "Filtros aplicados" })}</h4>
              {verificationResult.filters_applied && (
                <SpaceBetween size="xs">
                  <div>
                    <strong>{t("common.startDate", { defaultValue: "Fecha inicio" })}:</strong> {new Date(verificationResult.filters_applied.startDate).toLocaleDateString()}
                  </div>
                  <div>
                    <strong>{t("common.endDate", { defaultValue: "Fecha fin" })}:</strong> {new Date(verificationResult.filters_applied.endDate).toLocaleDateString()}
                  </div>
                  {verificationResult.filters_applied.supervisor && (
                    <div>
                      <strong>{t("insights.supervisor", { defaultValue: "Supervisor" })}:</strong> {verificationResult.filters_applied.supervisor}
                    </div>
                  )}
                  {verificationResult.filters_applied.agent && (
                    <div>
                      <strong>{t("insights.agent", { defaultValue: "Agente" })}:</strong> {verificationResult.filters_applied.agent}
                    </div>
                  )}
                </SpaceBetween>
              )}
            </Box>
            
            <p>{t("insights.confirmationPrompt", { defaultValue: "¿Desea continuar con el procesamiento de estos registros?" })}</p>
          </SpaceBetween>
        )}
      </Modal>

      {/* Modal para mostrar detalles del historial */}
      <Modal
        visible={showHistoryDetailModal}
        onDismiss={() => setShowHistoryDetailModal(false)}
        header={t("insights.historyDetails", { defaultValue: "Detalles del Análisis" })}
        size="max"
        footer={
          <Box float="right">
            <Button onClick={() => setShowHistoryDetailModal(false)} variant="primary">
              {t("common.close", { defaultValue: "Cerrar" })}
            </Button>
          </Box>
        }
      >
        {selectedHistoryItem && (
          <SpaceBetween size="l">
            <Container>
              <SpaceBetween size="s">
                <div>
                  <strong>{t("insights.historyId", { defaultValue: "ID del Análisis" })}:</strong> {selectedHistoryItem.historyId}
                </div>
                <div>
                  <strong>{t("insights.datetime", { defaultValue: "Fecha/Hora" })}:</strong> {selectedHistoryItem.datetime}
                </div>
                <div>
                  <strong>{t("insights.itemsAnalyzed", { defaultValue: "Items Analizados" })}:</strong> {selectedHistoryItem.itemsAnalyzed}
                </div>
              </SpaceBetween>
            </Container>
            
            <Container>
              <Header variant="h3">{t("insights.filtersApplied", { defaultValue: "Filtros Aplicados" })}</Header>
              <SpaceBetween size="s">
                <div>
                  <strong>{t("insights.dateRange", { defaultValue: "Rango de fechas" })}:</strong> {formatDateRange(selectedHistoryItem.filters)}
                </div>
                <div>
                  <strong>{t("insights.supervisor", { defaultValue: "Supervisor" })}:</strong> {selectedHistoryItem.filters?.supervisor || "Todos"}
                </div>
                <div>
                  <strong>{t("insights.agent", { defaultValue: "Agente" })}:</strong> {selectedHistoryItem.filters?.agent || "Todos"}
                </div>
              </SpaceBetween>
            </Container>
            
            <Container>
              <Header variant="h3">{t("insights.summary", { defaultValue: "Resumen Analítico Completo" })}</Header>
              <div style={{ 
                backgroundColor: "#f8f9fa", 
                padding: "20px", 
                borderRadius: "8px", 
                whiteSpace: "pre-wrap",
                lineHeight: "1.6",
                fontSize: "14px",
                fontFamily: "monospace",
                border: "1px solid #dee2e6",
                minHeight: "200px",
                maxHeight: "70vh",
                overflowY: "auto"
              }}>
                {selectedHistoryItem.summary || "No hay resumen disponible"}
              </div>
            </Container>
          </SpaceBetween>
        )}
      </Modal>
    </ContentLayout>
  );
}

export default InsightsCalls; 